/**
 * Shared calculation utilities for Analytics and Competitors tabs
 * Ensures consistent metrics calculations across the app
 */

import { subDays } from 'date-fns';
import { BASELINE_TOLERANCE_DAYS, countPostsAdded } from '@/utils/analyticsConstants';

export type TimeRange = 'week' | 'month';

export interface MetricDataPoint {
  date: string;
  followers: number;
  following: number;
  posts: number;
}

/**
 * Get the baseline date for a given time range
 * - week: 7 days ago
 * - month: 30 days ago
 */
export function getBaselineDate(timeRange: TimeRange): Date {
  const now = new Date();
  return subDays(now, timeRange === 'week' ? 7 : 30);
}

/**
 * Get the display start date (when the visible period begins)
 * - week: 7 days ago
 * - month: 30 days ago
 */
export function getDisplayStartDate(timeRange: TimeRange): Date {
  const now = new Date();
  return subDays(now, timeRange === 'week' ? 7 : 30);
}

/**
 * Find baseline data point for a given time range
 * Uses the most recent data point's date as reference (not new Date()),
 * matching useProfileMetrics logic for consistent calculations
 */
export function findBaselinePoint(
  metrics: MetricDataPoint[],
  timeRange: TimeRange
): MetricDataPoint | null {
  const sortedMetrics = [...metrics].sort((a, b) =>
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  // Use most recent data point's date as reference, not new Date()
  // This ensures consistent baseline regardless of when the last scrape ran
  const mostRecentDate = new Date(sortedMetrics[0].date);
  const daysBack = timeRange === 'week' ? 7 : 30;
  const baselineDate = subDays(mostRecentDate, daysBack);
  const toleranceDate = subDays(baselineDate, BASELINE_TOLERANCE_DAYS);
  const baselineTimestamp = baselineDate.getTime();
  const toleranceTimestamp = toleranceDate.getTime();

  const baseline = sortedMetrics.find(m => {
    const dataTimestamp = new Date(m.date).getTime();
    return dataTimestamp <= baselineTimestamp && dataTimestamp >= toleranceTimestamp;
  });

  // If no exact baseline found, fall back to oldest available data
  // This ensures users see progress as soon as they have 2+ data points
  if (!baseline && metrics.length >= 2) {
    return sortedMetrics[sortedMetrics.length - 1];
  }

  return baseline || null;
}

/**
 * Calculate period changes (followers, following, posts)
 * Returns null if insufficient data
 */
export function calculatePeriodChanges(
  metrics: MetricDataPoint[],
  timeRange: TimeRange
): { followers: number; following: number; posts: number } | null {
  if (metrics.length < 2) return null;

  const latest = metrics[0];
  const baseline = findBaselinePoint(metrics, timeRange);

  if (!baseline) return null;

  // Posts: count only additions (positive day-over-day deltas)
  const sortedMetrics = [...metrics].sort((a, b) =>
    new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  const baselineIdx = sortedMetrics.findIndex(m => m.date === baseline.date);
  const periodSlice = sortedMetrics.slice(Math.max(0, baselineIdx));

  return {
    followers: latest.followers - baseline.followers,
    following: latest.following - baseline.following,
    posts: countPostsAdded(periodSlice.map(m => m.posts)),
  };
}

/**
 * Calculate growth percentage
 * Returns null if no baseline or zero followers
 */
export function calculateGrowthPercent(
  metrics: MetricDataPoint[],
  timeRange: TimeRange
): number | null {
  if (metrics.length < 2) return null;

  const latest = metrics[0];
  const baseline = findBaselinePoint(metrics, timeRange);

  if (!baseline || baseline.followers <= 0) return null;

  const change = latest.followers - baseline.followers;
  return (change / baseline.followers) * 100;
}

/**
 * Format number with K/M suffix
 */
export function formatNumber(num: number | null | undefined): string {
  if (num === null || num === undefined) return '-';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

/**
 * Format change with +/- prefix
 */
export function formatChange(num: number | null | undefined): string {
  if (num === null || num === undefined) return '-';
  const sign = num > 0 ? '+' : '';
  return `${sign}${formatNumber(num)}`;
}
